import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { getFormData } from 'src/app/helpers/form.helper';
import {
  infiniteQueryOptions,
  InitialPageParam,
  onQueryStartedCreate,
  onQueryStartedDelete,
  onQueryStartedUpdate,
} from 'src/app/helpers/redux.helper';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import { ImportDataType } from 'src/app/redux/interests/interest-slice.type';
import {
  CompanyLookupDataType,
  CompanyLookupResultType,
  EnterpriseCreateDataType,
  EnterpriseGenerateReportFormDataType,
  EnterpriseIdentifiedDataType,
  EnterpriseIdentifiedResultType,
  EnterpriseUpdateDataType,
} from 'src/app/services/interest/Enterprise/enterprise-service.type';
import { EnterpriseInterestLookupDataType } from 'src/app/services/interest/interest.service.type';
import { finalUrlsConfig } from 'src/configs/url.config';

export const companySlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<CompanyLookupResultType, CompanyLookupDataType | void>({
      query: (params?: CompanyLookupDataType) => ({
        url: finalUrlsConfig().interest.recognized.enterprise,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'Companies' as const,
                id,
              })),
              { type: 'Companies', id: 'LIST' },
            ]
          : [{ type: 'Companies', id: 'LIST' }],
    }),
    getEnterprises: builder.infiniteQuery<
      EnterpriseIdentifiedResultType,
      EnterpriseInterestLookupDataType | void,
      InitialPageParam
    >({
      infiniteQueryOptions,
      query: ({ pageParam, queryArg }) => {
        return {
          url: finalUrlsConfig().interest.identified.enterprise.lookup,
          params: {
            ...pageParam,
            ...queryArg,
          },
        };
      },

      providesTags: [{ type: 'Enterprises', id: 'LIST' }],
    }),
    getEnterprisesAll: builder.query<EnterpriseIdentifiedResultType, EnterpriseInterestLookupDataType | void>({
      query: (params?: EnterpriseInterestLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.enterprise.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      transformResponse: (data: EnterpriseIdentifiedResultType) => {
        return {
          count: data.count,
          rows: data.rows.map((ent) => {
            if (ent.isProxy) {
              return {
                ...ent,
                company: {
                  ...ent.company,
                  name: `#${ent.company.name}`,
                },
              };
            }
            return ent;
          }),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'Enterprises' as const,
                id,
              })),
              { type: 'Enterprises', id: 'LIST' },
            ]
          : [{ type: 'Enterprises', id: 'LIST' }],
    }),
    createEnterprise: builder.mutation<EnterpriseIdentifiedDataType, EnterpriseCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.recognized.enterprise,
        method: 'POST',
        body: { ...data },
      }),
      onQueryStarted: async (enterpriseData, api) => onQueryStartedCreate(enterpriseData, api, 'getEnterprises'),
      invalidatesTags: [
        { type: 'AddSupplier', id: 'LIST' },
        { type: 'Companies', id: 'LIST' },
        // { type: 'Enterprises', id: 'LIST' },
        { type: 'Interests', id: 'LIST' },
      ],
    }),
    updateEnterprise: builder.mutation<EnterpriseIdentifiedDataType, EnterpriseUpdateDataType>({
      query: (data: EnterpriseUpdateDataType) => ({
        url: finalUrlsConfig().interest.identified.enterprise.update,
        method: 'PATCH',
        body: { ...data },
      }),
      onQueryStarted: async ({ id }, api) => onQueryStartedUpdate({ id }, api, 'getEnterprises'),

      invalidatesTags: (_result, _error, data: EnterpriseUpdateDataType) => [
        { type: 'AddSupplier', id: data.id },
        // { type: 'Enterprises', id: data.id },
        { type: 'Interactions', id: 'LIST' },
      ],
    }),
    deleteEnterprise: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.enterprise.delete}/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, api) => onQueryStartedDelete(id, api, 'getEnterprises'),
      invalidatesTags: (_result, _error, id) => [
        // { type: 'Enterprises', id },
        { type: 'Interests', id: 'LIST' },
      ],
    }),
    generateEnterpriseReport: builder.mutation<Blob, EnterpriseGenerateReportFormDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.enterprise.generateReport,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    exportEnterpriseIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.enterprise,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    importEnterprise: builder.mutation<void, ImportDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.enterprise.import,
        method: 'POST',
        body: getFormData(data, ['file']),
      }),

      invalidatesTags: [{ type: 'Enterprises', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreateEnterpriseMutation,
  useUpdateEnterpriseMutation,
  useGetEnterprisesAllQuery,
  useGenerateEnterpriseReportMutation,
  useGetCompaniesQuery,
  useDeleteEnterpriseMutation,
  useLazyGetCompaniesQuery,
  useExportEnterpriseIdentifiedExcellMutation,
  useLazyGetEnterprisesAllQuery,
  useGetEnterprisesInfiniteQuery,
  useImportEnterpriseMutation,
} = companySlice;
